import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Business, People } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';

const providerSteps = [
  {
    step: '1',
    title: 'Create your free provider account',
    body: 'Sign up free in minutes. Set your service areas, services, and rates — no forced monthly subscription to get started.',
  },
  {
    step: '2',
    title: 'Bring customers or win new leads',
    body: 'Onboard your existing book at 1%, or bid on marketplace requests when you want growth. Keep the relationships that matter.',
  },
  {
    step: '3',
    title: 'Schedule, run crews, and get paid',
    body: 'Use CRM, routing, crew tools, and automated payments in one place. Track performance and cash flow as you grow.',
  },
];

const customerSteps = [
  {
    step: '1',
    title: 'Request service',
    body: 'Submit your lawn care or snow removal needs and property details online.',
  },
  {
    step: '2',
    title: 'Compare bids',
    body: 'Review quotes from local providers along with ratings and reviews.',
  },
  {
    step: '3',
    title: 'Schedule and pay',
    body: 'Accept a bid, get the work done, and pay securely — including for family properties you manage remotely.',
  },
];

const HowItWorksPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'provider' | 'customer'>('provider');
  const steps = tab === 'provider' ? providerSteps : customerSteps;

  return (
    <>
      <SEO
        title="How LawnHub Works for Lawn Care Providers | LawnHub"
        description="See how LawnHub works: create an account, onboard customers or win leads, then schedule, run crews, and get paid — without a forced monthly subscription."
        keywords="how LawnHub works, lawn care software how it works, provider onboarding, lawn care CRM setup, schedule lawn care jobs"
        url="/how-it-works"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: 'How LawnHub works for lawn care providers',
          description:
            'Create a provider account, bring customers or bid on leads, then schedule crews and get paid on LawnHub.',
          step: providerSteps.map((s, i) => ({
            '@type': 'HowToStep',
            position: i + 1,
            name: s.title,
            text: s.body,
          })),
        }}
      />

      <Box sx={{ bgcolor: 'grey.50', py: { xs: 5, md: 7 }, borderBottom: 1, borderColor: 'divider' }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 800, color: 'primary.main', mb: 1.5, fontSize: { xs: '1.75rem', md: '2.5rem' } }}
          >
            How LawnHub Works
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, mb: 3, lineHeight: 1.5 }}>
            Built first for lawn care and snow removal businesses — with a simple path for homeowners too.
          </Typography>
          <Tabs
            value={tab === 'provider' ? 0 : 1}
            onChange={(_, v) => setTab(v === 0 ? 'provider' : 'customer')}
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minWidth: 160 },
            }}
          >
            <Tab icon={<Business />} iconPosition="start" label="For Providers" />
            <Tab icon={<People />} iconPosition="start" label="For Customers" />
          </Tabs>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 7 } }}>
        <Grid container spacing={3}>
          {steps.map((s) => (
            <Grid item xs={12} md={4} key={s.step}>
              <Paper sx={{ p: 4, height: '100%', textAlign: 'center' }} elevation={1}>
                <Typography variant="h3" color="primary" sx={{ fontWeight: 800, mb: 1 }}>
                  {s.step}
                </Typography>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                  {s.title}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {s.body}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 5, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={() =>
              navigate(tab === 'provider' ? '/register' : '/get-started', {
                state: tab === 'provider' ? { userType: 'provider' } : undefined,
              })
            }
            sx={{ bgcolor: '#ff6f00', fontWeight: 'bold', '&:hover': { bgcolor: '#ff8f00' } }}
          >
            {tab === 'provider' ? 'Create Free Provider Account' : 'Get Started Free'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate(tab === 'provider' ? '/provider-benefits' : '/customer-benefits')}
          >
            {tab === 'provider' ? 'Provider Benefits' : 'Customer Benefits'}
          </Button>
        </Box>
      </Container>
    </>
  );
};

export default HowItWorksPage;
